<template>
  <v-row class="new-user custom-scroll-bar">
    <v-snackbar
      v-model="snackbar"
      :timeout="snackbarTimeout"
      top
      :color="snackbarColor"
      rounded
      centered
    >
      {{ snackbarText }}
    </v-snackbar>
    <v-row style="width: 100%">
      <v-col cols="12" sm="4" class="profile-pic-col">
        <div class="load-img-container">
          <div class="load-img-btn-div">
            <v-btn
              class="load-img-btn"
              fab
              small
              @click="handleFileImport"
            >
              <v-icon>
                photo_camera
              </v-icon>
            </v-btn>
          </div>
          <input
            ref="uploader"
            class="d-none"
            type="file"
            accept="image/*"
            @change="onFileChanged"
          />
          <div class="load-img-div">
            <v-img class="profile-pic" id="profile-pic" height="124" width="124" :src="profilePicSrc"/>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <v-form v-model="isFormValid" class="mt-7" ref="newUserForm">
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                :label="`${$t('NewUser.name')} *`"
                :title="`${$t('NewUser.name')} *`"
                v-model="userData.name"
                outlined
                background-color="#FFF"
                color="#0098E1"
                requiredRules
                :rules="$ponceConfig.requiredRules"
                required
                maxlength="50"
                hide-details
                @blur="parseNames"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="pt-4">
            <v-col cols="12">
              <v-text-field
                :label="`${$t('NewUser.lastName')} *`"
                :title="`${$t('NewUser.lastName')} *`"
                v-model="userData.lastName"
                outlined
                background-color="#FFF"
                color="#0098E1"
                requiredRules
                :rules="$ponceConfig.requiredRules"
                required
                maxlength="50"
                hide-details
                @blur="parseNames"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" sm="4" class="text-right pt-6">
              <v-select
                :items="roles"
                item-value="value"
                :item-text="item => $t(`Roles.${item.value}`)"
                v-model="userData.role"
                :label="`${$t('NewUser.role')} *`"
                :title="`${$t('NewUser.role')} *`"
                :menu-props="{ bottom: true, offsetY: true }"
                outlined
                background-color="#FFF"
                color="#0098E1"
                :rules="$ponceConfig.requiredRules"
                required
                hide-details
                @change="roleChangeHandler"
              />
            </v-col>
            <v-col v-if="isAdmin" cols="12" offset-sm="1" sm="5" class="text-right pt-6">
              <v-autocomplete
                :items="organizations"
                item-value="value"
                :item-text="item => item.name"
                v-model="userData.organization"
                :label="$t('NewUser.organization')"
                :title="$t('NewUser.organization')"
                :menu-props="{ bottom: true, offsetY: true }"
                outlined
                background-color="#FFF"
                color="#0098E1"
                :rules="$ponceConfig.requiredRules"
                required
                hide-details
                :loading="loadingOrganizations"
                return-object
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <p class="section-text text-left pt-2">{{$t('GeneralPermissions.permissions')}}</p>
            </v-col>
          </v-row>
          <v-row no-gutters v-for="perm in getPermissionKeys" :key="`switch_${perm}`" class="permissions-elements flex-nowrap">
            <v-switch
              v-model="permissions[perm]"
              inset
              color="#00D166"
              :disabled="userData.permissionsCustomized"
            />
            <p class="mb-0 text-start switch-text">{{$t(`UsersPermissions.${perm}`)}}</p>
          </v-row>
          <v-row no-gutters class="pt-3">
            <v-col cols="12" sm="12">
              <VuePhoneNumberInput
                v-model="phoneNumberInput"
                color="#0098E1"
                size="lg"
                default-country-code="AR"
                :translations="phoneInputTranslations"
                @update="setPhoneNumber"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <v-row style="width: 100%" class="mt-6" justify="center">
      <v-col cols="12" sm="3">
        <v-btn
          @click="nextStep"
          block
          color="#0098E1"
          large
          class="text-none text-h6 font-weight-light"
          :loading="loading"
          :disabled="(!isFormValid || !isPhoneValid)"
          :dark="!(!isFormValid || !isPhoneValid)"
        >{{ fromNewCampaign || fromNewField ? $t('NewUser.register') : $t('NewUser.next')}}</v-btn>
        <v-btn
          @click="cancel"
          text
          small
          color="#0098E1"
          class="text-none text-body-2 font-weight-bold"
        >{{$t('NewUser.cancel')}}</v-btn>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
  import VuePhoneNumberInput from 'vue-phone-number-input'
  import 'vue-phone-number-input/dist/vue-phone-number-input.css'
  import {
    PROFILE_PIC_FILE_SIZE_LIMIT,
    ALLOWED_IMAGES_FILE_TYPES,
    ALLOWED_IMAGES_FILE_EXTENSIONS,
    WEBHOOKS_PATHS,
    USER_ROLES
  } from '../../../constants/const'
  import capitalize from 'lodash/capitalize'
  import get from 'lodash/get'
  import { getOrganizations } from '../../../lib/crudOperations/read'
  import { insertNewUser } from '../../../lib/crudOperations/write'

  export default {
    props: {
      userRegisterStep: {
        type: Number,
        required: true
      },
      userData: {
        type: Object,
        required: true
      },
      fromNewCampaign: {
        type: Boolean,
        required: true
      },
      originCampaignId: {
        type: String,
        required: false
      },
      newCampaignStep: {
        type: String,
        required: false
      },
      fromNewField: {
        type: Boolean,
        required: false
      },
      fromNewFieldData: {
        type: Object,
        required: false
      }
    },
    components: {
      VuePhoneNumberInput
    },
    data () {
      return {
        isFormValid: true,
        roles: this.getRoles(),
        phoneNumberInput: this.userData.phone ? this.userData.phone : '',
        phoneInputTranslations: {
          countrySelectorLabel: this.$t('NewUser.countryCode'),
          phoneNumberLabel: `${this.$t('NewUser.phoneNumber')} *`,
          example: this.$t('NewUser.example')
        },
        isPhoneValid: false,
        profilePicSrc: null,
        snackbar: false,
        snackbarTimeout: 3000,
        snackbarText: '',
        snackbarColor: '',
        loading: false,
        permissions: {
          reports: this.userData.reports || false,
          editDataPermission: this.userData.editDataPermission || false,
          audits: this.userData.audits || false
        },
        startHour: get(this.userData, 'settings.notificationsPeriod.startHour', '00:00'),
        endHour: get(this.userData, 'settings.notificationsPeriod.endHour', '23:59'),
        loadingOrganizations: false,
        organizations: [],
        isAdmin: get(this.$store, 'getters.userRoles.isAdmin', false)
      }
    },
    created () {
      this.setUserImg()
      this.isAdmin && this.getOrganizations()
    },
    methods: {
      setPhoneNumber (input) {
        this.isPhoneValid = input.isValid
        if (input && input.formattedNumber) {
          this.userData.phone = input.formattedNumber
        } else {
          this.userData.phone = ''
        }
      },
      handleFileImport () {
        this.$refs.uploader.click()
      },
      onFileChanged (e) {
        const file = e.target.files[0]
        if (file.size <= PROFILE_PIC_FILE_SIZE_LIMIT * 1024 * 1024 && ALLOWED_IMAGES_FILE_TYPES.includes(file.type)) {
          const imageSrc = URL.createObjectURL(file)
          this.profilePicSrc = imageSrc
          this.userData.profilePic = imageSrc
          this.userData.photoUrl = imageSrc
          this.$emit('update:userData', {
            ...this.userData,
            profilePic: imageSrc,
            photoUrl: imageSrc,
            profilePicFile: e.target.files[0]
          })
        } else {
          this.snackbar = true
          this.snackbarColor = '#F93A2F'
          if (!ALLOWED_IMAGES_FILE_TYPES.includes(file.type)) {
            this.snackbarText = this.$t('NewUser.allowedFileTypes', [ALLOWED_IMAGES_FILE_EXTENSIONS])
          } else {
            this.snackbarText = this.$t('NewUser.fileSizeLimit', [PROFILE_PIC_FILE_SIZE_LIMIT])
          }
        }
      },
      cancel () {
        if (this.fromNewCampaign) {
          this.$router.push({name: 'NewCampaignInitialData', params: {campaignId: this.originCampaignId, step: this.newCampaignStep}})
          return
        } else if (this.fromNewField) {
          this.$router.push({name: 'AddField', params: { ...this.fromNewFieldData }})
          return
        }

        this.$router.push({name: 'OrganizationHome'})
      },
      getRoles () {
        const roles = [...this.$ponceConfig.roles].filter(role => role.value !== USER_ROLES.OWNER)
        const user = this.$store.getters.user
        if (user.isAdmin && this.$store.getters.devUsers.includes(this.$store.getters.userAuth.user.email)) {
          roles.push({value: 'admin'})
        }
        return roles
      },
      roleChangeHandler () {
        this.setUserImg()
        this.userData.reports = this.userData.role === 'manager'
      },
      setUserImg () {
        if (this.userData.photoUrl) {
          this.profilePicSrc = this.userData.profilePic
        } else {
          switch (this.userData.role) {
            case 'admin':
            case 'owner':
              this.profilePicSrc = require('../../../assets/owner.png')
              break
            case 'manager':
              this.profilePicSrc = require('../../../assets/manager.png')
              break
            case 'fieldman':
              this.profilePicSrc = require('../../../assets/fieldman.png')
              break
            case 'farmhand':
              this.profilePicSrc = require('../../../assets/farmhand.png')
              break
            default:
              this.profilePicSrc = require('../../../assets/farmhand.png')
              break
          }
        }
      },
      async nextStep () {
        if (!this.loading) {
          try {
            this.loading = true
            const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.PHONE_EXISTS}?number=${encodeURIComponent(this.userData.phone)}`, {
              headers: {
                authorization: process.env.WEBHOOKS_BASIC_TOKEN
              }
            })
            const resData = await response.json()
            if (resData.exists === false) {
              if (this.fromNewCampaign) {
                await insertNewUser({
                  ...this.userData,
                  editCampaignDataPermission: true,
                  editDataPermission: true,
                  audits: this.permissions.audits
                }, [], [], this.originCampaignId)
                this.$router.push({name: 'NewCampaignInitialData', params: {campaignId: this.originCampaignId, step: this.newCampaignStep}})
                this.$root.snack.show({text: this.$t('UI.registerSuccess')})
                return
              } else if (this.fromNewField) {
                const newUserId = await insertNewUser({
                  ...this.userData,
                  editCampaignDataPermission: false,
                  editDataPermission: false,
                  audits: this.permissions.audits
                }, [], [], this.originCampaignId)
                this.fromNewFieldData.field[this.fromNewFieldData.contactPropertyName] = newUserId
                this.$router.push({name: 'AddField', params: { ...this.fromNewFieldData }})
                this.$root.snack.show({text: this.$t('UI.registerSuccess')})
                return
              }

              this.$emit('update:userData', {
                ...this.userData,
                reports: this.permissions.reports,
                editDataPermission: this.permissions.editDataPermission,
                audits: this.permissions.audits,
                settings: {
                  ...this.userData.settings,
                  notificationsPeriod: {
                    startHour: this.startHour,
                    endHour: this.endHour
                  }
                }
              })
              this.$emit('update:userRegisterStep', this.userRegisterStep + 1)
            } else {
              this.$root.snack.show({text: this.$t('UI.phoneAlreadyExists'), error: true})
            }
          } catch (error) {
            console.error(error)
            this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
          } finally {
            this.loading = false
          }
        }
      },
      parseNames () {
        this.userData.name = this.userData.name && this.userData.name.replace(/\s+/g, ' ').split(' ').map(string => capitalize(string)).join(' ')
        this.userData.lastName = this.userData.lastName && this.userData.lastName.replace(/\s+/g, ' ').split(' ').map(string => capitalize(string)).join(' ')
        console.log(this.userData)
      },
      setRoleDefaultPermissions (val) {
        let editDataPermission, reportsPermission, auditsPermission
        switch (val) {
          case 'farmhand':
            reportsPermission = false
            editDataPermission = false
            auditsPermission = false
            break
          case 'fieldman':
            reportsPermission = true
            editDataPermission = false
            auditsPermission = false
            break
          case 'manager':
            reportsPermission = true
            editDataPermission = true
            auditsPermission = false
            break
          case 'owner':
            reportsPermission = true
            editDataPermission = true
            auditsPermission = true
            break
          case 'admin':
            reportsPermission = true
            editDataPermission = true
            auditsPermission = true
            break
        }
        this.permissions.reports = reportsPermission
        this.permissions.editDataPermission = editDataPermission
        this.permissions.audits = auditsPermission
      },
      async getOrganizations () {
        try {
          if (this.$store.getters.organizations.length === 0) {
            this.loadingOrganizations = true
            const orgs = await getOrganizations()
            this.$store.dispatch('setOrganizations', orgs)
          }
          this.organizations = this.$store.getters.organizations
        } catch (error) {
          this.snackbar = true
          this.snackbarText = this.$t('NewUser.loadingOrganizationsError')
          this.snackbarColor = '#F93A2F'
        } finally {
          this.loadingOrganizations = false
        }
      }
    },
    computed: {
      getPermissionKeys () {
        if(this.fromNewCampaign || this.fromNewField){
          return Object.keys(this.permissions).filter(perm => perm !== 'editDataPermission')
        } else{
          return Object.keys(this.permissions)
        }
      }
    },
    watch: {
      'userData.role' (val) {
        this.setRoleDefaultPermissions(val)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// NewUser.vue?52453648